<script setup lang="ts">
import { isRunningInNativeApp, isRunningInTWA } from '~/utils/browserInfo';

type Props = {
  msToShow?: number;
};

const { msToShow = 5000 } = defineProps<Props>();

const { linkToAppDownload } = useLinks();
const { isDesktop, isLayoutMobileOrTablet } = useLayout();
const isAppEnvironment = isRunningInTWA() || isRunningInNativeApp();

const { isInstallAppPopupEnabled, isWithinMigrationPeriod } =
  useFeatureFlagsNew();

const { isStandAlone } = useLayout();
const { bottomMenu, menuOpen } = storeToRefs(useLayoutStore());
const { currentRouteName } = useRoutes();

// Detecção de bots para bypass automático
const { isBot } = usePerformanceBotBypass();

const bottomMenuHeight = computed(() => {
  const bottomMenuHeight = bottomMenu.value.height;

  return bottomMenuHeight ? `${bottomMenuHeight + 10}px` : '10px';
});

const cookieName = 'install_app_popup_closed';

const wasClosedPreviously = useCookie(cookieName, {
  default: () => false,
  path: '/',
  expires: new Date(new Date().setDate(new Date().getDate() + 7)),
  // expires: new Date(),
});

const isCountdownElapsed = ref(false);

const isVisible = computed(() => {
  return linkToAppDownload.value && !wasClosedPreviously.value;
});

const showInstallAppPopup = computed(() => {
  // Nunca mostrar popup para bots (Googlebot, Lighthouse, GTmetrix)
  if (isBot.value) {
    return false;
  }

  if (currentRouteName.value && currentRouteName.value?.includes('sports')) {
    return false;
  }

  return (
    isVisible.value &&
    !isStandAlone.value &&
    isInstallAppPopupEnabled.value &&
    (isLayoutMobileOrTablet.value ? !menuOpen.value : true) &&
    !isWithinMigrationPeriod.value &&
    !isAppEnvironment
  );
});

function setCookieClosed(e?: Event) {
  wasClosedPreviously.value = true;
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }
}

// O popup passou a ser só um atalho para a loja (ENG-843). Saiu daqui toda a
// instalação de PWA: o `beforeinstallprompt`, que oferecia instalar o site como
// aplicativo no Android, e o tutorial de "Adicionar à Tela de Início" do iOS.
// Este caminho já era o fallback de antes, quando não havia prompt disponível.
function openAppStore() {
  navigateTo(linkToAppDownload.value, {
    external: true,
    open: {
      target: '_blank',
    },
  });

  setCookieClosed();
}

onMounted(() => {
  if (!showInstallAppPopup.value) return;

  setTimeout(() => {
    isCountdownElapsed.value = true;
  }, msToShow);
});
</script>

<template>
  <button
    v-if="showInstallAppPopup"
    id="divInstallAppPopup"
    :class="[
      $style.installAppPopup,
      { [$style.isDesktop]: isDesktop },
      { [$style.isCountdownElapsed]: isCountdownElapsed },
      `transition`,
    ]"
    @click="openAppStore"
  >
    <div
      :class="$style.close"
      @click.prevent="setCookieClosed"
    >
      <NuxtIcon name="close" />
    </div>
    <div :class="$style.logoContainer">
      <BaseCommonLogo
        always-icon
        class="max-h-6 min-w-6 max-w-24 mr-0 !transform-none"
        custom-size
      />
    </div>
    <div :class="$style.textContainer">
      <h4>{{ $t('installAppPopup.title', { appName: appName() }) }}</h4>
      <p>
        {{ $t('installAppPopup.description') }}
        <img
          v-if="!isBot"
          :class="$style.iconGift"
          :src="getAssetsUrlForCDN('/assets/images/gift.png')"
        />
      </p>
    </div>
    <NuxtIcon
      :class="$style.iconArrow"
      name="arrows/angle_right"
    />
  </button>
</template>

<style module lang="postcss">
.installAppPopup {
  @apply z-[600] fixed inset-x-[10px] flex flex-row items-center gap-3 bg-bg-secondary rounded-lg py-2 px-3 shadow-lg shadow-black/20 overflow-hidden border-texts/10 border-solid border text-left cursor-pointer translate-y-20 opacity-0 transition-all duration-500;

  bottom: v-bind(bottomMenuHeight);

  &.isDesktop {
    @apply bottom-[12px] inset-x-[12px] left-auto min-w-[300px];
  }

  &.isCountdownElapsed {
    @apply translate-y-0 opacity-100;
  }

  &:before {
    @apply content-[''] absolute top-0 left-0 -z-10 w-full h-full bg-black/10;
  }

  &:hover {
    @apply opacity-100 brightness-125;

    &:before {
      @apply bg-black/20;
    }
  }

  .logoContainer {
    @apply flex items-center justify-center h-6 max-h-6 max-w-24 mr-0 overflow-hidden object-cover;
  }

  .textContainer {
    @apply leading-none flex flex-col;

    h4 {
      @apply font-bold text-xs text-texts;
    }

    p {
      @apply flex flex-row items-center gap-1 text-2xs text-texts;

      img {
        @apply w-[16px] h-[16px];
      }
    }
  }

  .iconGift {
    @apply w-[14px] h-[14px];
    animation: shake 3000ms linear infinite;
  }

  .close {
    @apply absolute top-1 right-1 flex items-center justify-center text-texts/50 text-2xs;

    &:hover {
      @apply text-texts scale-125;
    }
  }

  .iconArrow {
    @apply text-2xl text-texts ml-auto mr-1 my-auto;
    animation: arrowMove 800ms infinite;
  }
}

@keyframes shake {
  0% {
    transform: scale(1);
  }
  60% {
    transform: scale(0.9);
  }
  65% {
    transform: scale(1.1) skew(0.004turn);
  }
  70% {
    transform: scale(1) skew(0.008turn);
  }
  75% {
    transform: scale(1) skew(0);
  }
  77% {
    transform: rotateZ(-15deg);
  }
  79% {
    transform: rotateZ(10deg);
  }
  81% {
    transform: rotateZ(-10deg);
  }
  83% {
    transform: rotateZ(6deg);
  }
  85% {
    transform: rotateZ(-4deg);
  }
}

@keyframes arrowMove {
  0%,
  100% {
    transform: translateX(2px);
  }
  50% {
    transform: translateX(-2px);
  }
}
</style>
